import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { TagCloud } from './TagCloud';

interface Tag {
  name: string;
  count: number;
}

interface TagFilterProps {
  tags: Tag[];
  selectedTags: string[];
  onTagSelect: (tag: string) => void;
  personas?: any[];
}

export const TagFilter: React.FC<TagFilterProps> = ({
  tags,
  selectedTags,
  onTagSelect,
  personas = []
}) => {
  const { darkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [showTagCloud, setShowTagCloud] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  
  // Filter tags based on search query
  const filteredTags = searchQuery
    ? tags.filter(tag => tag.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : tags;
  
  // Determine how many tags to show
  const displayTags = expanded ? filteredTags : filteredTags.slice(0, 15);
  
  return (
    <div className={`tag-filter mb-6 ${
      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    } rounded-lg border transition-colors duration-200 overflow-hidden`}>
      <div 
        className={`flex justify-between items-center p-4 ${
          !isCollapsed ? (darkMode ? 'border-b border-gray-700' : 'border-b border-gray-200') : ''
        } cursor-pointer`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center">
          <h3 className="text-lg font-medium">Filter by Tags</h3>
          {selectedTags.length > 0 && (
            <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
              darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
            }`}>
              {selectedTags.length} selected
            </span>
          )}
        </div>
        <div className="flex items-center">
          <svg 
            className={`h-5 w-5 transition-transform ${isCollapsed ? '' : 'transform rotate-180'}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {/* Collapsible content */}
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
        isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
      }`}>
        <div className="p-4 pt-0">
          <div className="flex justify-between items-center mt-4 mb-3">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Search tags..."
                className={`w-full px-3 py-2 pl-9 border ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } rounded-md transition-colors duration-200`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowTagCloud(!showTagCloud);
              }}
              className={`ml-4 flex items-center text-sm font-medium ${
                darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              {showTagCloud ? 'Hide Tag Cloud' : 'Show Tag Cloud'}
              <svg 
                className={`ml-1 h-4 w-4 transition-transform ${showTagCloud ? 'transform rotate-180' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
      
          {/* Tag Cloud and Tag Filters in a two-column layout */}
          {showTagCloud && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Left column: Tag filters */}
              <div className="flex flex-col">
                <div className="flex flex-wrap gap-2 mb-2">
                  {displayTags.map(tag => (
                    <button
                      key={tag.name}
                      onClick={(e) => {
                        e.stopPropagation();
                        onTagSelect(tag.name);
                      }}
                      className={`px-3 py-1 rounded-full text-sm transition-all ${
                        selectedTags.includes(tag.name)
                          ? darkMode
                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-900/50'
                            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                          : darkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 shadow-inner shadow-gray-800 hover:shadow-md'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200 shadow-sm hover:shadow-md'
                      } hover:scale-105`}
                    >
                      {tag.name} <span className="text-xs opacity-80">({tag.count})</span>
                    </button>
                  ))}
                </div>
                
                {filteredTags.length > 15 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(!expanded);
                    }}
                    className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} text-sm font-medium mt-2`}
                  >
                    {expanded ? 'Show less' : `Show ${filteredTags.length - 15} more tags`}
                  </button>
                )}
              </div>
              
              {/* Right column: Tag Cloud visualization */}
              <div style={{ aspectRatio: '1/1' }}>
                <TagCloud 
                  tags={tags} 
                  onTagSelect={onTagSelect}
                  selectedTags={selectedTags}
                  personas={personas}
                />
              </div>
            </div>
          )}
          
          {/* Regular tag display when tag cloud is hidden */}
          {!showTagCloud && (
            <>
              <div className="flex flex-wrap gap-2 mb-2">
                {displayTags.map(tag => (
                  <button
                    key={tag.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTagSelect(tag.name);
                    }}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedTags.includes(tag.name)
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : darkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {tag.name} <span className="text-xs opacity-80">({tag.count})</span>
                  </button>
                ))}
              </div>
              
              {filteredTags.length > 15 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(!expanded);
                  }}
                  className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} text-sm font-medium`}
                >
                  {expanded ? 'Show less' : `Show ${filteredTags.length - 15} more tags`}
                </button>
              )}
            </>
          )}
      
          {selectedTags.length > 0 && (
            <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Selected Tags:</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    selectedTags.forEach(tag => onTagSelect(tag));
                  }}
                  className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'} text-xs`}
                >
                  Clear All
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedTags.map(tag => (
                  <div
                    key={`selected-${tag}`}
                    className={`${
                      darkMode 
                        ? 'bg-blue-900 text-blue-300 shadow-inner shadow-blue-950' 
                        : 'bg-blue-100 text-blue-800 shadow-sm'
                    } px-3 py-1 rounded-full text-sm flex items-center transition-all hover:shadow-md hover:scale-105`}
                  >
                    {tag}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onTagSelect(tag);
                      }}
                      className={`ml-2 ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
